import React, { createContext, useEffect, useState } from "react";
import productData from "../Datas/productData";

export const CartContext = createContext(null);

const CART_KEY = "ironheart.cart.v1";
const WISHLIST_KEY = "ironheart.wishlist.v1";

const readStorage = (key, fallback = []) => {
  if (typeof window === "undefined") return fallback;

  try {
    const saved = window.localStorage.getItem(key);
    return saved ? JSON.parse(saved) : fallback;
  } catch {
    return fallback;
  }
};

const normalizeCartItems = (items) => {
  if (!Array.isArray(items)) return [];

  return items
    .filter((item) => item && Number.isFinite(Number(item.id)))
    .map((item) => ({
      id: Number(item.id),
      size: String(item.size || "One Size"),
      color: String(item.color || "Default"),
      quantity: Math.max(1, Math.min(20, Number(item.quantity) || 1)),
    }));
};

const normalizeWishlist = (items) =>
  Array.isArray(items)
    ? [...new Set(items.map(Number).filter(Number.isFinite))]
    : [];

const getStockLimit = (productId) => {
  const stock = Number(
    productData.find((product) => product.id === productId)?.stock
  );
  return Number.isFinite(stock) && stock >= 0 ? stock : 20;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    const stored = readStorage(CART_KEY, null);
    const legacy = readStorage("cartItems", []);
    return normalizeCartItems(Array.isArray(stored) ? stored : legacy);
  });
  const [wishlist, setWishlist] = useState(() => {
    const stored = readStorage(WISHLIST_KEY, []);
    return normalizeWishlist(stored);
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(CART_KEY, JSON.stringify(cartItems));
    } catch {
      // Storage can be unavailable in privacy modes; in-memory state still works.
    }
  }, [cartItems]);

  useEffect(() => {
    try {
      window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(wishlist));
    } catch {
      // Storage can be unavailable in privacy modes; in-memory state still works.
    }
  }, [wishlist]);

  const addToCart = (productId, quantity = 1, size = "One Size", color = "Default") => {
    const safeQuantity = Math.max(1, Number(quantity) || 1);
    const stockLimit = getStockLimit(productId);
    if (stockLimit < 1) return;

    setCartItems((items) => {
      const match = items.find(
        (item) =>
          item.id === productId && item.size === size && item.color === color
      );

      if (!match) {
        return [
          ...items,
          { id: productId, size, color, quantity: Math.min(safeQuantity, stockLimit) },
        ];
      }

      return items.map((item) =>
        item.id === productId && item.size === size && item.color === color
          ? { ...item, quantity: Math.min(stockLimit, item.quantity + safeQuantity) }
          : item
      );
    });
  };

  const removeFromCart = (productId, size, color) => {
    setCartItems((items) =>
      items.filter(
        (item) =>
          !(item.id === productId && item.size === size && item.color === color)
      )
    );
  };

  const updateQuantity = (productId, size, color, quantity) => {
    const safeQuantity = Number(quantity);
    const stockLimit = getStockLimit(productId);
    if (safeQuantity <= 0 || stockLimit < 1) {
      removeFromCart(productId, size, color);
      return;
    }

    setCartItems((items) =>
      items.map((item) =>
        item.id === productId && item.size === size && item.color === color
          ? { ...item, quantity: Math.min(stockLimit, safeQuantity) }
          : item
      )
    );
  };

  const clearCart = () => setCartItems([]);

  const addToWishlist = (productId) => {
    setWishlist((items) => (items.includes(productId) ? items : [...items, productId]));
  };

  const removeFromWishlist = (productId) => {
    setWishlist((items) => items.filter((id) => id !== productId));
  };

  const toggleWishlist = (productId) => {
    setWishlist((items) =>
      items.includes(productId)
        ? items.filter((id) => id !== productId)
        : [...items, productId]
    );
  };

  const value = {
    cartItems,
    wishlist,
    cartCount: cartItems.reduce((sum, item) => sum + item.quantity, 0),
    addToCart,
    addCart: addToCart,
    removeFromCart,
    removeCart: removeFromCart,
    updateQuantity,
    clearCart,
    addToWishlist,
    removeFromWishlist,
    toggleWishlist,
    isWishlisted: (productId) => wishlist.includes(productId),
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
